import { computed, effect, Injectable, signal } from '@angular/core';
import { LoggingService } from '@app/core/services';
import { AuthStore } from '@app/core/state/auth.store';
import { TimezoneStore } from '@app/core/state/timezone-store/timezone-store.service';

import { AppSettingsDto, UploaderSettingsDto, UserInfoDto } from '../../app-settings/models/app-settings.model';

@Injectable({ providedIn: 'root' })
export class AppSettingsStore {
    readonly settings = signal<AppSettingsDto | null>(null);
    readonly isLoading = signal<boolean>(false);

    // Computed signal for timezone
    readonly currentTimeZone = computed(() => {
        const settings = this.settings();
        return settings?.tenantTimeZoneIana
            || settings?.currentTimeZone
            || this.timezoneStore.currentTimeZone
            || this.detectBrowserTimeZone();
    });

    // ==== Global/UI state signals migrated from GlobalStateStore ====
    private _shouldRefreshTables = signal<number>(0);
    private _fileItemCount = signal<number>(0);
    private _isRegistered = signal<boolean>(false);
    private _activeComponent = signal<number>(1);

    readonly fileItemCount = this._fileItemCount.asReadonly();
    readonly shouldRefreshTables = this._shouldRefreshTables.asReadonly();
    readonly isRegistered = this._isRegistered.asReadonly();
    readonly activeComponent = this._activeComponent.asReadonly();


    constructor(
        private loggingService: LoggingService,
        private authStore:        AuthStore,
        private timezoneStore:    TimezoneStore
    ) {
        effect(() => {
            const activeComp = this._activeComponent();
            this.loggingService.debug(
                `[AppSettingsStore] activeComponent updated to ${activeComp}`
            );
        });

    }

    private applyTenantTimeZone(settings: AppSettingsDto | null): void {
        if (!settings) {
            return;
        }

        const resolvedZone = settings.tenantTimeZoneIana || settings.currentTimeZone;
        if (!resolvedZone) {
            return;
        }

        if (resolvedZone === this.timezoneStore.currentTimeZone) {
            return;
        }

        this.timezoneStore.setTimeZone(resolvedZone);
        this.loggingService.debug('[AppSettingsStore] Applied tenant timezone', resolvedZone);
    }

    private protectAuthenticatedIdentifiers(user: UserInfoDto): UserInfoDto {
        const authenticatedUserId = this.authStore.getUserId()?.trim() || '';
        if (!authenticatedUserId) {
            return user;
        }

        const incomingUserId = user.id?.trim() || '';
        const incomingTenantId = user.clientId?.trim() || '';
        const isSameUser = !incomingUserId || incomingUserId === authenticatedUserId;
        const safeIncomingTenantId =
            isSameUser && incomingTenantId.toLowerCase() !== 'system' ? incomingTenantId : '';

        return {
            ...user,
            id: authenticatedUserId,
            subject: this.authStore.getSubject()?.trim() || authenticatedUserId,
            clientId: this.authStore.getClientId()?.trim() || safeIncomingTenantId,
        };
    }

    /** Settings may seed an empty bootstrap store, but never replace auth-owned identity. */
    private seedMissingAuthenticatedIdentifiers(user: UserInfoDto): void {
        const userId = user.id?.trim() || '';
        const tenantId = user.clientId?.trim() || '';
        const currentUserId = this.authStore.getUserId()?.trim() || '';

        if (!currentUserId && userId) {
            this.authStore.setSelectedUser(userId);
        }

        const resolvedUserId = currentUserId || userId;
        if (!this.authStore.getClientId() && tenantId && tenantId.toLowerCase() !== 'system' && resolvedUserId === userId) {
            this.authStore.setSelectedClient(tenantId);
        }
    }

     readonly uploaderSettings = computed<UploaderSettingsDto | null>(() => {
        const settings = this.settings();
        return settings?.uploaderSettings || null;
    });


    updateSettings(settings: AppSettingsDto) {
        const protectedSettings = settings.user
            ? { ...settings, user: this.protectAuthenticatedIdentifiers(settings.user) }
            : settings;

        this.settings.set(protectedSettings);
        this.applyTenantTimeZone(protectedSettings);

        if (protectedSettings.user) {
            this.seedMissingAuthenticatedIdentifiers(protectedSettings.user);
        }

        this.loggingService.info('[AppSettingsStore] Settings updated.', protectedSettings);
    }

    updateUserInfo(userInfo: UserInfoDto) {
        const currentSettings = this.settings();
        const protectedUserInfo = this.protectAuthenticatedIdentifiers(userInfo);
        const nextSettings = currentSettings
            ? { ...currentSettings, user: protectedUserInfo }
            : ({ user: protectedUserInfo } as AppSettingsDto);

        this.settings.set(nextSettings);
        this.loggingService.info('[AppSettingsStore] updateUserInfo: user info set', {
            username: protectedUserInfo.name,
            clientId: protectedUserInfo.clientId,
        });

        this.seedMissingAuthenticatedIdentifiers(protectedUserInfo);
        this.applyTenantTimeZone(nextSettings);
    }

    /**
     * Clears the stored UserInfo in the application state
     */
    clearUserInfo(): void {
        const currentSettings = this.settings();
        if (!currentSettings?.user) {
            return;
        }

        const { user: _user, ...settingsWithoutUser } = currentSettings;
        this.settings.set(settingsWithoutUser);
        this.loggingService.info('[AppSettingsStore] clearUserInfo: user info cleared');
    }

    startLoading() {
        this.isLoading.set(true);
        this.loggingService.debug('[AppSettingsStore] Loading started');
    }

    stopLoading() {
        this.isLoading.set(false);
        this.loggingService.debug('[AppSettingsStore] Loading stopped');
    }

    /* UI/global signals & methods */

    setShouldRefreshTables(): void {
        this._shouldRefreshTables.update((current) => current + 1);
    }

    setFileItemCount(count: number) {
        this._fileItemCount.set(count);
    }

    setIsRegistered(isRegistered: boolean): void {
        this._isRegistered.set(isRegistered);
        this.loggingService.debug('GlobalStateStore: isRegistered updated to', isRegistered);
    }

    setActiveComponent(component: number): void {
        this._activeComponent.set(component);
    }

    getFileItemCount(): number {
        return this._fileItemCount();
    }

    /* Timezone methods */

    /**
     * Detect browser's IANA timezone identifier
     */
    private detectBrowserTimeZone(): string {
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return timeZone && timeZone.length > 0 ? timeZone : 'UTC';
        } catch (error) {
            this.loggingService.error('[AppSettingsStore] Error detecting browser timezone:', error);
            return 'UTC';
        }
    }

    /**
     * Convert UTC date string to local date string for display
     */
    public convertUtcToLocal(utcDateString: string | null | undefined): Date | null {
        if (!utcDateString) {
            return null;
        }

        try {
            const utcDate = new Date(utcDateString);

            if (isNaN(utcDate.getTime())) {
                this.loggingService.error('[AppSettingsStore] Invalid date string:', utcDateString);
                return null;
            }

            return utcDate;
        } catch (error) {
            this.loggingService.error('[AppSettingsStore] Error converting date:', error);
            return null;
        }
    }

    /**
     * Format UTC date for display in user's timezone
     */
    public formatForDisplay(
        utcDateString: string | null | undefined,
        format: 'short' | 'medium' | 'long' | 'full' = 'medium'
    ): string {
        const date = this.convertUtcToLocal(utcDateString);

        if (!date) {
            return '';
        }

        const timeZone = this.currentTimeZone();

        try {
            const options: Intl.DateTimeFormatOptions = this.getFormatOptions(format);

            return new Intl.DateTimeFormat('en-US', {
                ...options,
                timeZone: timeZone
            }).format(date);
        } catch (error) {
            this.loggingService.error('[AppSettingsStore] Error formatting date:', error);
            return date.toLocaleString();
        }
    }

    /**
     * Get offset string (e.g., "UTC+8", "UTC-5")
     */
    public getOffsetString(): string {
        const fallback = 'UTC';
        const resolvedZone =
            this.currentTimeZone()
            || this.timezoneStore.currentTimeZone
            || this.detectBrowserTimeZone()
            || fallback;

        try {
            const fromShortName = this.tryParseOffsetFromShortName(resolvedZone);
            if (fromShortName) {
                return fromShortName;
            }

            const minutes = this.calculateOffsetMinutes(resolvedZone);
            if (minutes !== null) {
                return this.formatOffsetMinutes(minutes);
            }
        } catch (error) {
            this.loggingService.warn('[AppSettingsStore] Failed to resolve timezone offset', {
                zone: resolvedZone,
                error
            });
        }

        return fallback;
    }

    private tryParseOffsetFromShortName(timeZone: string): string | null {
        try {
            const now = new Date();
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone,
                hour12: false,
                timeZoneName: 'shortOffset'
            }).formatToParts(now);

            const offsetToken = parts.find((p) => p.type === 'timeZoneName')?.value;
            if (!offsetToken) {
                return null;
            }

            const match = offsetToken.match(/([+-])(\d{1,2})(?::?(\d{2}))?/);
            if (!match) {
                if (offsetToken.toUpperCase() === 'UTC' || offsetToken.toUpperCase() === 'GMT') {
                    return 'UTC';
                }

                return null;
            }

            const [, sign, hoursPart, minutesPart] = match;
            const hours = hoursPart.padStart(2, '0');
            const minutes = (minutesPart ?? '00').padStart(2, '0');

            return minutes === '00'
                ? `UTC${sign}${hours}`
                : `UTC${sign}${hours}:${minutes}`;
        } catch {
            return null;
        }
    }

    private calculateOffsetMinutes(timeZone: string): number | null {
        try {
            const now = new Date();
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone,
                hour12: false,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });

            const parts = formatter.formatToParts(now);
            const year = Number(parts.find((p) => p.type === 'year')?.value);
            const month = Number(parts.find((p) => p.type === 'month')?.value);
            const day = Number(parts.find((p) => p.type === 'day')?.value);
            const hour = Number(parts.find((p) => p.type === 'hour')?.value);
            const minute = Number(parts.find((p) => p.type === 'minute')?.value);
            const second = Number(parts.find((p) => p.type === 'second')?.value);

            if (
                Number.isNaN(year)
                || Number.isNaN(month)
                || Number.isNaN(day)
                || Number.isNaN(hour)
                || Number.isNaN(minute)
                || Number.isNaN(second)
            ) {
                return null;
            }

            const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
            const diffMinutes = Math.round((asUtc - now.getTime()) / 60000);
            return diffMinutes;
        } catch (error) {
            this.loggingService.warn('[AppSettingsStore] Unable to calculate offset minutes', {
                zone: timeZone,
                error
            });
            return null;
        }
    }

    private formatOffsetMinutes(offsetMinutes: number): string {
        const sign = offsetMinutes >= 0 ? '+' : '-';
        const absoluteMinutes = Math.abs(offsetMinutes);
        const hours = Math.floor(absoluteMinutes / 60)
            .toString()
            .padStart(2, '0');
        const minutes = absoluteMinutes % 60;

        if (minutes === 0) {
            return `UTC${sign}${hours}`;
        }

        return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
    }

    /**
     * Get format options based on format type
     */
    private getFormatOptions(format: 'short' | 'medium' | 'long' | 'full'): Intl.DateTimeFormatOptions {
        switch (format) {
            case 'short':
                return {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                };
            case 'medium':
                return {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                };
            case 'long':
                return {
                    year: 'numeric',
                    month: 'long',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    timeZoneName: 'short'
                };
            case 'full':
                return {
                    year: 'numeric',
                    month: 'long',
                    day: '2-digit',
                    weekday: 'long',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    timeZoneName: 'long'
                };
            default:
                return {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                };
        }
    }

}
